import React, { useMemo, useCallback } from 'react';
import Table from 'src/components/Table';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import { createWalkthroughTableColumns } from './helpers';
import { ITableColumn } from 'src/components/Table/types';
import Checkbox from 'src/components/Checkbox';
import { IWalkthroughModel } from 'src/services/client-types';
import { WalkthroughActions } from 'src/components/BulkOperations/Walkthroughs/BulkActivationWalkthroughsDialogue/BulkActivateWalkthroughsDialogue';
import './BulKWalkthroughActivationList.scss';

interface BulkWalkthroughsActivationListProps {
  walkthroughsByUnit: Map<string, IWalkthroughModel[]>;
  selectedWalkthroughIds: Set<string>;
  walkthroughActions: WalkthroughActions;
  suites: UnitBulkOperationDto[];
  sendSchedulingEmail: boolean;
}

interface UnitTableData {
  unitId: string;
  suite: UnitBulkOperationDto | undefined;
  selectableWalkthroughs: IWalkthroughModel[];
  columns: ITableColumn[];
}

const BulkWalkthroughsActivationList = ({
  walkthroughsByUnit,
  selectedWalkthroughIds,
  walkthroughActions,
  suites,
  sendSchedulingEmail,
}: BulkWalkthroughsActivationListProps) => {
  const {
    toggle,
    selectAllForUnit,
    deselectAllForUnit,
    selectAll,
    deselectAll,
  } = walkthroughActions;

  const suiteLookup: Map<string, UnitBulkOperationDto> = useMemo(() => {
    const map = new Map<string, UnitBulkOperationDto>();
    suites.forEach((suite: UnitBulkOperationDto) => map.set(suite.id, suite));
    return map;
  }, [suites]);

  const areAllWalkthroughsSelected = useCallback(
    (walkthroughs: IWalkthroughModel[]): boolean => {
      const selectable: IWalkthroughModel[] = walkthroughs.filter(
        (w: IWalkthroughModel) => {
          if (!sendSchedulingEmail && w.status !== 'Activate') {
            return false;
          }

          return true;
        }
      );

      if (selectable.length === 0) {
        return false;
      }

      return selectable.every((walkthrough: IWalkthroughModel) =>
        selectedWalkthroughIds.has(walkthrough.id)
      );
    },
    [selectedWalkthroughIds, sendSchedulingEmail]
  );

  const calculateSelectableWalkthroughs = useCallback(
    (walkthroughs: IWalkthroughModel[]): IWalkthroughModel[] => {
      return walkthroughs.filter((walkthrough: IWalkthroughModel) => {
        if (walkthrough.status === 'Schedule') {
          return false;
        }

        if (!sendSchedulingEmail && walkthrough.status !== 'Activate') {
          return false;
        }

        return true;
      });
    },
    [sendSchedulingEmail] // Add dependency
  );

  const unitEntries = useMemo(
    () => [...walkthroughsByUnit],
    [walkthroughsByUnit]
  );

  const globalAllSelected: boolean = useMemo(() => {
    const allWalkthroughs: IWalkthroughModel[] = [
      ...walkthroughsByUnit.values(),
    ].flat();

    const selectableWalkthroughs: IWalkthroughModel[] =
      calculateSelectableWalkthroughs(allWalkthroughs);

    return (
      selectableWalkthroughs.length > 0 &&
      areAllWalkthroughsSelected(selectableWalkthroughs)
    );
  }, [
    walkthroughsByUnit,
    calculateSelectableWalkthroughs,
    areAllWalkthroughsSelected,
  ]);

  const handleGlobalSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked: boolean = e.target.checked;

      if (isChecked) {
        selectAll();
      } else {
        deselectAll();
      }
    },
    [selectAll, deselectAll]
  );

  const unitTableData: UnitTableData[] = useMemo(() => {
    return unitEntries.map(([unitId, walkthroughs]) => {
      const suite: UnitBulkOperationDto = suiteLookup.get(unitId);

      const allWalkthroughsSelected: boolean =
        walkthroughs.length > 0 && areAllWalkthroughsSelected(walkthroughs);

      const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked: boolean = e.target.checked;

        if (isChecked) {
          selectAllForUnit(walkthroughs);
        } else {
          deselectAllForUnit(walkthroughs);
        }
      };

      const columns: ITableColumn[] = createWalkthroughTableColumns({
        allSelected: allWalkthroughsSelected,
        onSelectAll: handleSelectAll,
        selectedWalkthroughIds,
        onWalkthroughSelect: toggle,
        sendSchedulingEmail,
      });

      return {
        unitId,
        suite,
        selectableWalkthroughs: walkthroughs,
        columns,
      };
    });
  }, [
    unitEntries,
    suiteLookup,
    calculateSelectableWalkthroughs,
    areAllWalkthroughsSelected,
    selectedWalkthroughIds,
    toggle,
    selectAllForUnit,
    deselectAllForUnit,
  ]);

  if (unitEntries.length === 0) {
    return (
      <div className="activation-list-empty">
        Select a unit above to view its walkthroughs.
      </div>
    );
  }

  return (
    <div className="activation-list-container">
      {/* Global Select All Checkbox. */}
      <div className="global-select-all">
        <Checkbox
          checked={globalAllSelected}
          value={globalAllSelected}
          onChange={handleGlobalSelectAll}
        />
        <span className="global-select-all-label">
          Select all walkthroughs listed below
        </span>
      </div>
      {unitTableData.map(
        ({ unitId, suite, selectableWalkthroughs, columns }: UnitTableData) => (
          <div key={unitId} className="unit-walkthrough-group">
            <div className="unit-walkthrough-header">
              <span className="unit-number">
                Suite {suite?.number || 'Unknown'}
              </span>
            </div>
            <div className="unit-walkthrough-table-wrapper">
              <Table
                columns={columns}
                data={selectableWalkthroughs}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default BulkWalkthroughsActivationList;
